/**
 * Safe localStorage utility to handle SecurityError when localStorage is not accessible
 * (e.g., in cross-origin iframes, private browsing, etc.)
 */

/**
 * Safely get an item from localStorage
 * @param {string} key - The localStorage key
 * @param {*} defaultValue - Default value to return if access fails
 * @returns {*} The stored value or defaultValue
 */
export function getItem(key, defaultValue = null) {
    try {
        return localStorage.getItem(key);
    } catch (error) {
        console.warn(`localStorage.getItem('${key}') failed:`, error.message);
        return defaultValue;
    }
}

/**
 * Safely set an item in localStorage
 * @param {string} key - The localStorage key
 * @param {string} value - The value to store
 * @returns {boolean} True if successful, false otherwise
 */
export function setItem(key, value) {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (error) {
        console.warn(`localStorage.setItem('${key}', '${value}') failed:`, error.message);
        return false;
    }
}

/**
 * Safely remove an item from localStorage
 * @param {string} key - The localStorage key
 * @returns {boolean} True if successful, false otherwise
 */
export function removeItem(key) {
    try {
        localStorage.removeItem(key);
        return true;
    } catch (error) {
        console.warn(`localStorage.removeItem('${key}') failed:`, error.message);
        return false;
    }
}

/**
 * Check if localStorage is available
 * @returns {boolean} True if localStorage is accessible, false otherwise
 */
export function isAvailable() {
    try {
        const testKey = '__localStorage_test__';
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Safely get and parse JSON from localStorage
 * @param {string} key - The localStorage key
 * @param {*} defaultValue - Default value to return if access fails or JSON is invalid
 * @returns {*} The parsed JSON value or defaultValue
 */
export function getJSON(key, defaultValue = null) {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
        console.warn(`getJSON('${key}') failed:`, error.message);
        return defaultValue;
    }
}

/**
 * Safely stringify and set JSON in localStorage
 * @param {string} key - The localStorage key
 * @param {*} value - The value to stringify and store
 * @returns {boolean} True if successful, false otherwise
 */
export function setJSON(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
        return true;
    } catch (error) {
        console.warn(`setJSON('${key}', value) failed:`, error.message);
        return false;
    }
}
